import { useEffect, useRef, useState } from "react";

const LINKS = [
  "Group Overview",
  "Investments",
  "About Petter",
  "Hotels & Franchise",
  "Press Pictures",
  "Request & Contact",
];

export default function BigFooter() {
  const year = new Date().getFullYear();
  const ref = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <footer
      ref={ref}
      className="w-full"
      style={{
        padding: "40px 40px 40px",
        background: "#000",
        fontFamily: "'Space Grotesk', system-ui, sans-serif",
      }}
    >
      <div
        className="mx-auto relative overflow-hidden"
        style={{
          maxWidth: 1440,
          background: "#2563EB",
          borderRadius: 24,
          padding: "48px 48px 40px",
          color: "#ECECEC",
          opacity: inView ? 1 : 0,
          transform: inView ? "translateY(0)" : "translateY(40px)",
          transition:
            "opacity 0.9s cubic-bezier(0.2, 0.8, 0.2, 1), transform 0.9s cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}
      >
        {/* Animated background orbs */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: "#3B82F6",
            top: -220,
            right: -160,
            opacity: 0.35,
            filter: "blur(2px)",
            animation: "footerOrb 12s ease-in-out infinite",
          }}
        />
        <div
          aria-hidden
          style={{
            position: "absolute",
            width: 340,
            height: 340,
            borderRadius: "50%",
            background: "#93C5FD",
            bottom: -160,
            left: -80,
            opacity: 0.22,
            animation: "footerOrb 15s ease-in-out infinite reverse",
          }}
        />

        <div className="relative">
          {/* Top bar */}
          <div className="flex items-center justify-between">
            <div style={{ fontSize: 18, fontWeight: 700 }}>Strawberry Group</div>
            <ContactBtn />
          </div>

          {/* Big links */}
          <div className="mt-14 flex flex-col" style={{ gap: 4 }}>
            {LINKS.map((label, i) => {
              const isHover = hoveredLink === label;
              const anyHover = hoveredLink !== null;
              return (
                <a
                  key={label}
                  href="#"
                  className="cursor-pointer relative flex items-center"
                  onMouseEnter={() => setHoveredLink(label)}
                  onMouseLeave={() => setHoveredLink(null)}
                  style={{
                    fontSize: "clamp(36px, 5.5vw, 84px)",
                    fontWeight: 700,
                    letterSpacing: "-0.03em",
                    lineHeight: 1.05,
                    color: "#ECECEC",
                    textDecoration: "none",
                    opacity: inView ? (anyHover && !isHover ? 0.35 : 1) : 0,
                    transform: inView
                      ? isHover
                        ? "translateX(28px)"
                        : "translateX(0)"
                      : "translateX(-40px)",
                    transition: `opacity 0.35s ease ${
                      inView ? 0 : i * 0.06
                    }s, transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) ${
                      inView ? 0 : 0.2 + i * 0.06
                    }s`,
                  }}
                >
                  {/* Arrow that slides in on hover */}
                  <span
                    aria-hidden
                    style={{
                      position: "absolute",
                      left: -60,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 44,
                      height: 44,
                      opacity: isHover ? 1 : 0,
                      transform: isHover
                        ? "translateX(0) scale(1)"
                        : "translateX(-16px) scale(0.6)",
                      transition:
                        "opacity 0.35s ease, transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)",
                      color: "#93C5FD",
                    }}
                  >
                    <i
                      className="ri-arrow-right-up-line"
                      style={{ fontSize: 40 }}
                    />
                  </span>
                  <span>{label}</span>
                </a>
              );
            })}
          </div>

          {/* Bottom info */}
          <div
            className="mt-16 grid gap-8"
            style={{ gridTemplateColumns: "1.2fr 1fr 1.4fr 1fr" }}
          >
            {[
              {
                label: "Cookie Preferences",
                content: (
                  <>
                    © {year} Strawberry Group.
                    <br />
                    All rights reserved.
                  </>
                ),
              },
              {
                label: "Address",
                content: (
                  <>
                    Frederik Stangs gate 22–24
                    <br />
                    0264 Oslo, Norway
                    <br />
                    PO Box 2424 Solli, 0201 Oslo
                  </>
                ),
              },
              {
                label: "Press & media",
                content: (
                  <>
                    bettina@strawberry.no
                    <br />
                    kenneth.hultgren@strawberry.se
                    <br />
                    johannes.banggren@strawberry.no
                  </>
                ),
              },
              {
                label: "Investment opportunities",
                content: (
                  <a
                    href="#"
                    style={{
                      color: "#ECECEC",
                      textDecoration: "underline",
                    }}
                  >
                    Send pitch
                  </a>
                ),
              },
            ].map((block, i) => (
              <div
                key={block.label}
                style={{
                  opacity: inView ? 1 : 0,
                  transform: inView ? "translateY(0)" : "translateY(20px)",
                  transition: `opacity 0.7s ease ${
                    0.6 + i * 0.08
                  }s, transform 0.7s ease ${0.6 + i * 0.08}s`,
                }}
              >
                <div style={{ fontSize: 13, opacity: 0.55 }}>{block.label}</div>
                <div
                  style={{
                    marginTop: 14,
                    fontSize: 13,
                    opacity: 0.9,
                    lineHeight: 1.55,
                  }}
                >
                  {block.content}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes footerOrb {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50%      { transform: translate(-30px, 20px) scale(1.08); }
        }
      `}</style>
    </footer>
  );
}

function ContactBtn() {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="whitespace-nowrap cursor-pointer relative overflow-hidden inline-flex items-center gap-2"
      style={{
        background: "#0B1B4D",
        color: hover ? "#0B1B4D" : "#93C5FD",
        border: "none",
        borderRadius: 9999,
        padding: "11px 22px",
        fontWeight: 600,
        fontSize: 14,
        transition: "color 0.35s ease",
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background: "#93C5FD",
          borderRadius: 9999,
          transform: hover ? "translateY(0)" : "translateY(100%)",
          transition: "transform 0.45s cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}
      />
      <span style={{ position: "relative" }}>Contact Us</span>
      <span
        aria-hidden
        style={{
          position: "relative",
          display: "inline-flex",
          width: 16,
          height: 16,
          alignItems: "center",
          justifyContent: "center",
          transform: hover ? "translate(3px, -3px)" : "translate(0, 0)",
          transition: "transform 0.35s ease",
        }}
      >
        <i className="ri-arrow-right-up-line" style={{ fontSize: 14 }} />
      </span>
    </button>
  );
}